import { useCurrentShopSuspense } from "@/api/shops/use-current-shop-suspense";
import { Button } from "@babackapp/ui/components/button";
import { Tabs, TabsContent } from "@babackapp/ui/components/tabs";
import FormGlobalError from "@babackapp/react-hook-forms-components/form-global-error";
import FormSaveButton from "@babackapp/react-hook-forms-components/form-save-button";
import useTranslation from "@babackapp/translations/use-translation";
import { Form } from "@babackapp/ui/components/form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import ExportFormTabs from "./export-form-tabs";
import ExportOptionsForm from "./export-options-form";
import ExportServerForm from "./export-server-form";

const AddExportSettingsFormSchema = z.object({
  server: z.object({
    serverType: z.enum(["ftp", "sftp"]),
    host: z.string(),
    user: z.string(),
    password: z.string(),
    path: z.string(),
  }),
  options: z.object({
    cron: z.string(),
    modelId: z.string(),
    resolutions: z.array(z.string()),
    statuses: z.array(z.string()),
    locationIds: z.array(z.string()),
    returnStoreIds: z.array(z.string()),
  }),
});

export type ExportSettingsFormValues = z.infer<
  typeof AddExportSettingsFormSchema
>;

type ExportSettingsFormProps = {
  onValidate?: (
    values: ExportSettingsFormValues["server"],
  ) => Promise<void> | void;
  onSubmit: (values: ExportSettingsFormValues) => void;
  onRemove?: () => void;
  defaultValues?: ExportSettingsFormValues;
};

export default function ExportSettingsForm({
  onValidate,
  onSubmit,
  onRemove,
  defaultValues,
}: ExportSettingsFormProps) {
  const [error, setError] = useState<string | null>(null);
  const { data: shop } = useCurrentShopSuspense();
  const models = shop.exportModels;
  const t = useTranslation();

  const form = useForm<ExportSettingsFormValues>({
    resolver: zodResolver(AddExportSettingsFormSchema),
    defaultValues: {
      server: {
        serverType: "ftp",
        host: "",
        user: "",
        password: "",
        path: "",
        ...defaultValues?.server,
      },
      options: {
        cron: "0 10 * * *",
        modelId: models?.[0]?.id,
        resolutions: [],
        statuses: [],
        locationIds: [],
        returnStoreIds: [],
        ...defaultValues?.options,
      },
    },
  });

  const _onSubmit = async (data: ExportSettingsFormValues) => {
    try {
      await onValidate?.(data.server);
      return onSubmit(data);
    } catch (error: unknown) {
      // @ts-expect-error TODO: fix this
      setError(error?.message || "Unknown error");
    }
  };

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(_onSubmit)}
        className="flex gap-8 flex-col"
      >
        {error && <FormGlobalError error={error} />}
        <Tabs defaultValue="server">
          <ExportFormTabs />
          <TabsContent value="server">
            <ExportServerForm />
          </TabsContent>
          <TabsContent value="options">
            <ExportOptionsForm />
          </TabsContent>
        </Tabs>
        <div className="flex gap-2 justify-center items-center">
          {onRemove && (
            <Button variant="destructive">{t("misc.delete")}</Button>
          )}
          <FormSaveButton />
        </div>
      </form>
    </Form>
  );
}
